import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatchApp } from "../../../../lib/redux";
import {
  startConfirmBrandInsightApply,
  startProposeBrandInsightApply,
  startWarmUpProposeBrandInsightApply,
} from "../../../../actions/brandLearnedInsights";
import { startGetBrandInformation } from "../../../../actions/adsCampaigns";
import { BRAND_INSIGHT_ALLOWLIST_FIELDS } from "../../../../utils/constants";
import { LaunchError } from "../../../../utils/errors";
import {
  createApplyProposeSession,
  reduceApplyProposeResult,
} from "../../../../utils/brandInsightApplyPropose";

const pickCurrentFields = (brand = {}) => {
  const currentFields = {};
  BRAND_INSIGHT_ALLOWLIST_FIELDS.forEach((field) => {
    currentFields[field] = brand[field] ?? "";
  });
  return currentFields;
};

/**
 * Propose → review modal → confirm apply to brand setup.
 * Playbook polarity/index is optional: the Insights page passes them so the
 * insight is dismissed; the report apply does not.
 * Pass { source: "global_report" } so the backend curates the raw report card
 * before sending it through the same propose/validate path as playbook insights.
 */
export const useApplyBrandInsight = () => {
  const dispatch = useDispatchApp();
  const [applyInsight, setApplyInsight] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [isProposing, setIsProposing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [previousBrand, setPreviousBrand] = useState(null);
  const sessionRef = useRef(null);
  const applySourceRef = useRef(undefined);
  if (sessionRef.current === null) {
    sessionRef.current = createApplyProposeSession();
  }

  const runPropose = useCallback(
    async (insight, source) => {
      const session = sessionRef.current;
      const { requestId, signal } = session.start();
      setProposal(null);
      setApplyError("");
      setIsProposing(true);
      try {
        const brandResponse = await dispatch(startGetBrandInformation());
        if (!session.isCurrent(requestId)) return;
        const brand = brandResponse?.data || {};
        setPreviousBrand(brand);
        const response = await dispatch(
          startProposeBrandInsightApply({
            insight,
            currentFields: pickCurrentFields(brand),
            source,
            signal,
          })
        );
        const reduced = reduceApplyProposeResult({
          isCurrent: session.isCurrent(requestId),
          result: response,
        });
        if (!reduced.ignored) {
          setApplyError(reduced.applyError);
          setProposal(reduced.proposal);
        }
      } catch (error) {
        if (!session.isCurrent(requestId)) return;
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useApplyBrandInsight",
            context: {},
            tags: { action: "proposeBrandInsightApply" },
          })
        );
        setApplyError("rejected");
      } finally {
        if (session.finish(requestId)) {
          setIsProposing(false);
        }
      }
    },
    [dispatch]
  );

  const openApply = useCallback(
    async (insight, { source } = {}) => {
      applySourceRef.current = source;
      setApplyInsight(insight);
      await runPropose(insight, source);
    },
    [runPropose]
  );

  const retryApply = useCallback(async () => {
    if (!applyInsight) {
      return;
    }
    await runPropose(applyInsight, applySourceRef.current);
  }, [applyInsight, runPropose]);

  const closeApply = useCallback(() => {
    const closed = sessionRef.current.close();
    setIsProposing(closed.isProposing);
    setApplyInsight(null);
    setIsConfirming(false);
  }, []);

  useEffect(() => () => sessionRef.current?.abort(), []);

  // Boots the propose function on mount: a cold start costs 17-20s and the user
  // spends that time reading before pressing Aplicar. Fire-and-forget on
  // purpose — it must never surface an alert or a Sentry event.
  useEffect(() => {
    dispatch(startWarmUpProposeBrandInsightApply());
  }, [dispatch]);

  const confirmApply = useCallback(async () => {
    if (!proposal || !applyInsight) {
      return { ok: false };
    }
    setIsConfirming(true);

    const result = await dispatch(
      startConfirmBrandInsightApply({
        field: proposal.field,
        proposedValue: proposal.proposedValue,
        previousBrand,
        polarity: applyInsight.polarity,
        index: applyInsight.index,
      })
    );
    if (result?.ok) {
      closeApply();
    } else {
      setIsConfirming(false);
      setApplyError("confirm-failed");
    }
    return result;
  }, [applyInsight, closeApply, dispatch, previousBrand, proposal]);

  return {
    applyInsight,
    proposal,
    isProposing,
    isConfirming,
    applyError,
    openApply,
    closeApply,
    confirmApply,
    retryApply,
  };
};
